import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Seo from "./Seo";
import { SITE } from "./config";
import { Phone, ArrowRight, Calculator } from "lucide-react";

// Mapping volume → benne recommandée
const BENNE_RECO = [
  { max: 3, label: "3 m³", desc: "petits travaux de bricolage / débarras léger" },
  { max: 5, label: "5 m³", desc: "rénovation d'une pièce (salle de bain, cuisine)" },
  { max: 8, label: "8 m³", desc: "rénovation d'un appartement F2/F3" },
  { max: 12, label: "10 m³", desc: "rénovation d'une maison ou gros chantier" },
  { max: 18, label: "15 m³", desc: "petite démolition / chantier moyen" },
  { max: 25, label: "20 m³", desc: "gros chantier industriel / paysager" },
  { max: 35, label: "30 m³", desc: "déchets volumineux non denses" },
  { max: 999, label: "40 m³", desc: "très gros volumes (cartons, plastiques)" },
];

export function Calculateur() {
  const [length, setLength] = useState(4);
  const [width, setWidth] = useState(3);
  const [height, setHeight] = useState(0.3);
  const volume = (length * width * height).toFixed(2);
  const reco = BENNE_RECO.find((b) => Number(volume) <= b.max) || BENNE_RECO[BENNE_RECO.length - 1];

  return (
    <div>
      <Seo title="Calculateur de volume — Quelle benne choisir ?" description="Calculez en 30 secondes le volume de vos déchets et trouvez la benne adaptée. Outil gratuit FB Environnement, Saint-Victoret (13)." path="/calculateur-volume" />
      <section className="bg-gradient-to-br from-[#1F6B2E] to-[#0f3a18] text-white">
        <div className="max-w-4xl mx-auto px-4 py-14 sm:py-20">
          <span className="inline-block bg-[#9DCB2E] text-[#0f3a18] font-black uppercase tracking-[0.15em] text-xs px-3 py-1.5 rounded-full">Outil gratuit</span>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight mt-5">Quelle benne pour quel volume ?</h1>
          <p className="text-stone-100 text-lg mt-4">Estimez en 30 secondes le volume de vos déchets et trouvez la benne adaptée.</p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-4 py-14">
        <div className="bg-white border border-stone-100 rounded-3xl p-7">
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "Longueur (m)", val: length, set: setLength },
              { label: "Largeur (m)", val: width, set: setWidth },
              { label: "Hauteur (m)", val: height, set: setHeight },
            ].map((f) => (
              <label key={f.label} className="block">
                <span className="text-xs uppercase tracking-wider font-bold text-stone-500">{f.label}</span>
                <input type="number" min="0" step="0.1" value={f.val} onChange={(e) => f.set(parseFloat(e.target.value) || 0)} className="w-full mt-1 bg-stone-50 border border-stone-200 rounded-2xl px-4 py-3 font-bold" />
              </label>
            ))}
          </div>
          <div className="mt-7 pt-7 border-t border-stone-100">
            <div className="flex items-baseline justify-between">
              <span className="text-sm text-stone-500 font-bold uppercase tracking-wider">Volume estimé</span>
              <span className="font-heading font-black text-4xl text-[#1F6B2E]">{volume} m³</span>
            </div>
            <div className="mt-5 bg-[#9DCB2E]/20 border border-[#9DCB2E] rounded-2xl p-4">
              <div className="text-xs uppercase tracking-wider font-bold text-[#0f3a18]">Benne recommandée</div>
              <div className="font-heading font-black text-2xl text-[#0f3a18] mt-1">{reco.label}</div>
              <p className="text-sm text-stone-700 mt-1">Idéale pour {reco.desc}.</p>
            </div>
            <div className="flex flex-wrap gap-3 mt-6">
              <a href={`tel:${SITE.phoneRaw}`} className="inline-flex items-center gap-2 bg-[#1F6B2E] text-white font-bold px-5 py-3 rounded-full"><Phone className="w-4 h-4" /> {SITE.phone}</a>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-white border border-stone-200 text-[#0f3a18] font-bold px-5 py-3 rounded-full">Devis benne <ArrowRight className="w-4 h-4" /></Link>
            </div>
          </div>
        </div>
        <div className="mt-6 text-sm text-stone-500 leading-relaxed">
          <p><strong>Astuce</strong> : majorez de 20% le volume calculé pour anticiper l&apos;effet de tassement partiel des déchets. Pour les gravats lourds (béton, carrelage), tablez plutôt sur une benne 5 m³ par tonne — le poids est limitant avant le volume.</p>
        </div>
      </section>
    </div>
  );
}

const GLOSSARY = [
  { term: "Gravats", def: "Déchets inertes issus de la construction ou démolition : béton, briques, parpaings, tuiles, carrelage. Recyclés en granulats pour BTP." },
  { term: "DIB", def: "Déchets Industriels Banals — déchets non dangereux non inertes mélangés (cartons, plastiques, mousses). Triés et valorisés en filière." },
  { term: "Tout-venant", def: "Mélange de déchets non dangereux. Coût plus élevé car nécessite un tri post-collecte." },
  { term: "Plâtre & placo", def: "Plaques de plâtre, BA13, faux plafonds. Filière spécifique car le plâtre génère du sulfure d'hydrogène mélangé aux gravats." },
  { term: "Mélange bitumineux", def: "Enrobé, asphalte, croûtes de chaussée. Filière dédiée — interdiction de mélanger avec les gravats." },
  { term: "Déchets verts", def: "Tontes, branches, feuilles, taille de haies. Valorisés en compost ou broyat." },
  { term: "Souches & troncs", def: "Bois lourd et volumineux issu de l'élagage ou abattage. Broyage spécifique nécessaire." },
  { term: "Bordereau de suivi", def: "Document légal attestant la prise en charge et le traitement des déchets. Obligatoire pour les pros." },
  { term: "Tonnage", def: "Poids du déchet déposé. Mesuré sur pont-bascule à l'entrée et à la sortie." },
  { term: "Valorisation", def: "Transformation des déchets en matière première secondaire (recyclage) ou énergie. Mesurée par le taux de valorisation." },
  { term: "Filière agréée", def: "Centre de tri respectant la réglementation environnementale ICPE. FB Environnement est une filière agréée Bouches-du-Rhône." },
];

export function Glossaire() {
  return (
    <div>
      <Seo title="Glossaire des déchets pros — Lexique du recyclage" description="Définitions claires : gravats, DIB, tout-venant, plâtre, bordereau de suivi, valorisation. Lexique FB Environnement, centre de recyclage Saint-Victoret." path="/glossaire" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "DefinedTermSet",
          name: "Glossaire des déchets professionnels",
          hasDefinedTerm: GLOSSARY.map((g) => ({ "@type": "DefinedTerm", name: g.term, description: g.def })),
        })}</script>
      </Helmet>
      <section className="bg-gradient-to-br from-[#1F6B2E] to-[#0f3a18] text-white">
        <div className="max-w-4xl mx-auto px-4 py-14 sm:py-20">
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Glossaire des déchets pros</h1>
          <p className="text-stone-100 text-lg mt-4">Le vocabulaire du recyclage pro, expliqué simplement.</p>
        </div>
      </section>
      <section className="max-w-3xl mx-auto px-4 py-14 space-y-4">
        {GLOSSARY.map((g) => (
          <div key={g.term} className="bg-white border border-stone-100 rounded-2xl p-5">
            <h2 className="font-heading font-black text-xl text-[#1F6B2E]">{g.term}</h2>
            <p className="text-stone-700 mt-1">{g.def}</p>
          </div>
        ))}
      </section>
    </div>
  );
}

const FAQ_ITEMS = [
  { q: "Quels déchets pros acceptez-vous ?", a: "Gravats, plâtre & placo, mélange bitumineux, déchets verts, souches et troncs, bois, DIB (cartons, plastiques), archives confidentielles, pneus VL & PL. Les déchets dangereux (amiante, peintures, solvants) ne sont pas acceptés." },
  { q: "Faut-il prendre un rendez-vous ?", a: "Non. Le centre FB Environnement de Saint-Victoret est ouvert sans rendez-vous du lundi au vendredi, 7h-17h non-stop. Pour les très gros volumes (> 10 tonnes), un coup de fil au préalable est apprécié pour préparer la pesée." },
  { q: "Comment se passe la pesée ?", a: "Vous passez sur le pont-bascule à l&apos;entrée (poids véhicule chargé), puis à la sortie (poids véhicule vide). Le tonnage déposé = différence des deux pesées. Tarif calculé au kg, facturé HT." },
  { q: "Vous livrez des bennes ?", a: "Oui, sur toute la zone PACA. Bennes de 3 m³ à 40 m³ livrées sous 24-48h. Devis transparent : livraison + location + retrait + traitement + bordereau de suivi inclus." },
  { q: "Vous donnez un bordereau de suivi ?", a: "Oui, sur demande. Document légal attestant le traitement de vos déchets, obligatoire pour les pros (BSD si déchet dangereux, BSDA si amiante)." },
  { q: "Vos tarifs sont-ils négociables ?", a: "Les tarifs au tonnage sont publics et identiques pour tous. Pour les pros à fort volume mensuel récurrent, des conditions adaptées peuvent être discutées : appelez-nous." },
  { q: "Vous travaillez avec des particuliers ?", a: "Oui, mais notre cœur de métier reste les pros (BTP, paysagisme, artisans, industriels). Les particuliers sont accueillis sans difficulté pour des dépôts ponctuels." },
  { q: "Comment payer ?", a: "Espèces, CB, virement. Facture émise à chaque dépôt. Comptes pro ouverts sur demande après vérification Kbis." },
];

export function FaqPros() {
  return (
    <div>
      <Seo title="FAQ — Questions fréquentes des pros sur le recyclage" description="Vos questions sur le tri, le tonnage, la pesée, les bennes et les tarifs. FB Environnement, centre de recyclage agréé à Saint-Victoret." path="/faq-dechets-pros" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ_ITEMS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        })}</script>
      </Helmet>
      <section className="bg-gradient-to-br from-[#1F6B2E] to-[#0f3a18] text-white">
        <div className="max-w-4xl mx-auto px-4 py-14 sm:py-20">
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Questions fréquentes</h1>
          <p className="text-stone-100 text-lg mt-4">Tout ce que les pros nous demandent souvent.</p>
        </div>
      </section>
      <section className="max-w-3xl mx-auto px-4 py-14 space-y-3">
        {FAQ_ITEMS.map((f, i) => (
          <details key={i} className="bg-white border border-stone-100 rounded-2xl p-5 group">
            <summary className="font-heading font-black text-lg cursor-pointer flex items-start gap-2">
              <span className="text-[#1F6B2E]">→</span> {f.q}
            </summary>
            <p className="text-stone-700 mt-3 leading-relaxed">{f.a}</p>
          </details>
        ))}
      </section>
    </div>
  );
}

export function Comparatif() {
  return (
    <div>
      <Seo title="Centre de tri ou benne livrée — Quel choix pour vos déchets ?" description="Comparatif détaillé : apporter ses déchets au centre de Saint-Victoret VS faire livrer une benne. Avantages, inconvénients, prix." path="/comparatif" />
      <section className="bg-gradient-to-br from-[#1F6B2E] to-[#0f3a18] text-white">
        <div className="max-w-4xl mx-auto px-4 py-14 sm:py-20">
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tight">Centre ou benne livrée ?</h1>
          <p className="text-stone-100 text-lg mt-4">Choisissez la solution qui colle vraiment à votre chantier.</p>
        </div>
      </section>
      <section className="max-w-5xl mx-auto px-4 py-14 grid md:grid-cols-2 gap-5">
        <div className="bg-white border border-stone-100 rounded-3xl p-7">
          <h2 className="font-heading font-black text-2xl text-[#1F6B2E]">Dépôt au centre</h2>
          <p className="text-stone-600 mt-2 text-sm">Vous chargez et vous venez à Saint-Victoret.</p>
          <ul className="mt-4 space-y-2 text-sm text-stone-700">
            <li>✓ <strong>Moins cher</strong> au tonnage</li>
            <li>✓ Pas de réservation, vous venez quand vous voulez</li>
            <li>✓ Bordereau remis sur place</li>
            <li>✕ Vous devez avoir un véhicule adapté</li>
            <li>✕ Trajet à votre charge</li>
            <li>✕ Pas pratique pour les très gros volumes</li>
          </ul>
          <div className="mt-5 text-xs uppercase tracking-wider font-bold text-stone-500">Idéal pour</div>
          <p className="text-sm font-bold text-[#0f3a18]">petits volumes, dépôts récurrents, pros locaux</p>
        </div>
        <div className="bg-white border border-stone-100 rounded-3xl p-7">
          <h2 className="font-heading font-black text-2xl text-[#1F6B2E]">Benne livrée</h2>
          <p className="text-stone-600 mt-2 text-sm">On dépose une benne sur votre chantier.</p>
          <ul className="mt-4 space-y-2 text-sm text-stone-700">
            <li>✓ <strong>Vous ne bougez pas</strong> du chantier</li>
            <li>✓ Volume important d&apos;un coup (jusqu&apos;à 40 m³)</li>
            <li>✓ Vous remplissez à votre rythme</li>
            <li>✓ Tracabilité complète</li>
            <li>✕ Prix plus élevé (livraison + retrait)</li>
            <li>✕ Réservation nécessaire</li>
          </ul>
          <div className="mt-5 text-xs uppercase tracking-wider font-bold text-stone-500">Idéal pour</div>
          <p className="text-sm font-bold text-[#0f3a18]">rénovation lourde, démolition, chantier de plus d&apos;une semaine</p>
        </div>
      </section>
      <section className="max-w-4xl mx-auto px-4 pb-16">
        <div className="bg-[#9DCB2E]/20 border border-[#9DCB2E] rounded-3xl p-6">
          <h3 className="font-heading font-black text-xl text-[#0f3a18]">La règle simple à retenir</h3>
          <p className="text-stone-700 mt-2">En dessous de <strong>3 m³ et 1 tonne</strong>, le dépôt au centre est presque toujours plus rentable. Au-dessus, la benne devient progressivement plus pratique. À partir de <strong>5 m³</strong> sur un chantier qui dure plus d&apos;une semaine, la benne livrée est quasi imbattable.</p>
        </div>
      </section>
    </div>
  );
}
